/**
 * myDayReminder.js
 *
 * Local "My day" re-reminder: one pending expo-notifications notification per user (deterministic identifier
 * `myday:snooze:<userId>`, so scheduling is idempotent). Reuses the existing Android `calendar-alarm` channel from
 * {@link module:helpers/calendarReminders}. Tapping it foregrounds the app, whose AppState handler then finds the
 * snooze due and reopens the briefing. No-op on web; never throws.
 *
 * @module helpers/myDayReminder
 */

import * as Notifications from 'expo-notifications';
import {Platform} from 'react-native';
import {ALARM_CHANNEL_ID, ensureAlarmChannel} from './calendarReminders';
import {reminderIdentifier} from './myDayBriefing';

const available = () => Platform.OS !== 'web'
    && Boolean(Notifications)
    && typeof Notifications.scheduleNotificationAsync === 'function'
    && typeof Notifications.cancelScheduledNotificationAsync === 'function';

/**
 * Cancel the user's pending reminder.
 * @param {string} userId user id. @returns {Promise<void>}
 */
export const cancelMyDayReminder = async (userId) => {
    if (!userId || !available()) {
        return;
    }

    try {
        await Notifications.cancelScheduledNotificationAsync(reminderIdentifier(userId));
    } catch (_e) {
        // Nothing pending / best effort.
    }
};

/**
 * Schedule (replace) the user's reminder at `dueAt`.
 * @param {string} userId user id.
 * @param {number} dueAt epoch ms in the future.
 * @param {{title: string, body: string}} text localized notification text.
 * @returns {Promise<boolean>} true when scheduled.
 */
export const scheduleMyDayReminder = async (userId, dueAt, {title, body}) => {
    if (!userId || !available() || !(dueAt > Date.now())) {
        return false;
    }

    try {
        await cancelMyDayReminder(userId);
        await ensureAlarmChannel();
        await Notifications.scheduleNotificationAsync({
            identifier: reminderIdentifier(userId),
            content: {
                title,
                body,
                sound: 'default',
                ...(Platform.OS === 'ios' ? {interruptionLevel: 'timeSensitive', threadIdentifier: 'myday'} : {}),
                data: {type: 'myday', eventKey: 'myday.reminder', userId: String(userId), fireAt: String(dueAt)},
            },
            trigger: {
                type: Notifications.SchedulableTriggerInputTypes?.DATE || 'date',
                date: new Date(dueAt),
                ...(Platform.OS === 'android' ? {channelId: ALARM_CHANNEL_ID} : {}),
            },
        });

        return true;
    } catch (_e) {
        return false;
    }
};
